/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import React, {FocusEvent, useCallback} from 'react';
import {makeStyles, Typography} from '@material-ui/core';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import FormItem from '@ion/dls/src/components/forms/FormItem';
import FormNumberField from '@ion/dls/src/components/forms/FormNumberField';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {PegFieldProps} from '../../types';
import {isValidMin} from './PegNumericSlider';

const useStyles = makeStyles(() => ({
  numericStepperFormItem : {
    width    : '100%',
    maxWidth : 'none'
  },
  validRange : {
    display  : 'inline-block',
    color    : dlsColor('grey3'),
    fontSize : '0.875rem'
  }
}));

type NumericCandidate = number | boolean | string | undefined;

const MAX_DECIMAL_PLACES = 2;

// well beyond any displayed precision, yet enough to absorb floating-point drift...
const DRIFT_PRECISION = 10;

const isNumericValue = (value: NumericCandidate): boolean => (
  !isNil(value) && typeof value !== 'boolean' && value !== '' && Number.isFinite(Number(value))
);

const countDecimals = (num: number): number => {
  const [mantissa, exponent] = num.toString().split('e-');
  const mantissaPlaces = mantissa.split('.')[1]?.length ?? 0;
  return exponent ? mantissaPlaces + Number(exponent) : mantissaPlaces;
};

/**
 * Infers a numeric field's precision as the largest number of decimal places
 * among the given values, capped at 2 (e.g. `2.25` gives `2`, `2.89999` gives
 * `2` and `2` gives `0`). Non-numeric and nil values contribute nothing.
 */
export const getDecimalPlaces = (...values: NumericCandidate[]): number => (
  Math.min(MAX_DECIMAL_PLACES, values.reduce<number>((maxPlaces, value) => (
    isNumericValue(value) ? Math.max(maxPlaces, countDecimals(Number(value))) : maxPlaces
  ), 0))
);

/**
 * Truncates a numeric value to the given number of decimal places (e.g.
 * `2.89999` to 2 places gives `2.89`). The floating-point drift accumulated
 * across repeated stepper increments is rounded away first, so a drifted
 * `3.1499999999999995` still yields `3.15` rather than `3.14`.
 */
export const truncateToPrecision = (value: number | string, decimals: number): number => {
  const [integer, fraction = ''] = Number(value).toFixed(DRIFT_PRECISION).split('.');
  return Number(`${integer}.${fraction.slice(0, decimals) || '0'}`);
};

/**
 * Defines the `PegNumericStepper` component, which renders a numeric input field with step controls.
 * The Step arrows allow users to increment or decrement values within a defined range.
 * 
 * ```typescript
 *  
 *  <PegNumericStepper includeStepper
 *    fieldLabel={label}
 *    value={5}
 *    min={0}
 *    max={10}
 *    onChange={function(){}} />
 * 
 * ```
 */
const PegNumericStepper = ({label, unit, value, onChange, min, max, disabled, defaultValue}: PegFieldProps) => {
  const classes  = useStyles();
  const decimals = getDecimalPlaces(defaultValue, min, max);

  const handleBlur = useCallback((evt: FocusEvent<HTMLInputElement>) => {
    if (isValidMin(evt, min)) {
      onChange?.(Number(min));
    }
  }, [onChange, min]);

  return (
    <FormItem className={classes.numericStepperFormItem}>
      <FormNumberField fieldLabel={`${label} ${unit ? `(${unit})` : ''}`}
        suffix={unit}
        value={truncateToPrecision(value as number | string, decimals)}
        min={min}
        max={max}
        decimalScale={decimals}
        includeStepper
        allowedRange
        disabled={disabled}
        onChange={function({target: {value: v}}) {
          onChange?.(truncateToPrecision(v, decimals));
        }}
        onBlur={handleBlur} />
      <Typography className={classes.validRange}>
        {translate('peg-wizard:stepsCard.wakeSleep.validRange', {min, max})}
      </Typography>
    </FormItem>
  );
};

export {PegNumericStepper};
